import { idleSummaryFacts } from '@evi/shared';
import { IdleSummaryLine } from '@evi/ui';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Calendar, X, ExternalLink, Loader2, ChevronDown, MapPin, Check, FileArchive, Wallet, Sparkles, Ticket, Download } from 'lucide-react';
import {
  BottomSheet, RevenueChart, RevenuePeriodPill,
  ExportActions as SharedExportActions,
} from '@evi/ui';
import { NonDriverBanner } from '@/components/NonDriverBanner';
import { StatusPill } from '@/components/primitives';
import { useCurrentMode } from '@/hooks/useCurrentMode';
import { toast } from '@/store/toast';
import {
  fetchMySessions, fetchSession, fetchChargingSummary,
  fetchMyChargingRevenueSeries,
  downloadSessionsCsv, downloadSessionsReportPdf, downloadReceiptsBundle,
  fetchMyActivity, fetchMyActivitySummary,
  fetchSessionPoints, sessionPointsNote,
  formatCents,
  type SessionData, type SessionDetail, type SessionPoints, type ChargingSummary,
  type MyChargingRevenueSeries,
  type ActivityType, type ActivityEntry, type ActivitySummary,
  type ActivityWalletTopupPayload, type ActivityPointsPayload,
  type ActivityCouponPayload,
} from '@/lib/api';
import { downloadSessionReceipt } from '@/lib/receipt-pdf';
import { sessionReceiptPath } from '@/lib/sessionReceiptLink';
import { sessionNeverStartedNoCharge } from '@/lib/sessionPayment';
import { sessionWalletFxLine } from '@/lib/sessionWalletFx';
import { formatMoneyExplicit } from '@/lib/money';
import { useHomeSession } from '@/hooks/useHomeSession';
import NamedIcon from '@/components/ui/NamedIcon';
import styles from './HistoryPage.module.css';

interface Props { onNavigate: (path: string) => void }

type Filter = 'all' | 'completed' | 'cancelled';
type SessionStatus = SessionData['status'];

/**
 * Sort options on the history list. Applied client-side via
 * useMemo over the filtered+period set — server already returns
 * sessions in started_at desc order; non-default sorts re-order
 * locally. With limit:100 from the sessions query, the in-memory
 * sort is cheap.
 */
type Sort =
  | 'newest'
  | 'oldest'
  | 'cost-desc'
  | 'cost-asc'
  | 'energy-desc'
  | 'energy-asc'
  | 'duration-desc'
  | 'duration-asc'
  | 'location-asc';

const DEFAULT_SORT: Sort = 'newest';

/** Maps each sort mode to its i18n key under the `sort` group.
 *  Resolve the display label via t(SORT_LABELS[mode]) at render. */
const SORT_LABELS: Record<Sort, string> = {
  newest: 'sort.newest',
  oldest: 'sort.oldest',
  'cost-desc': 'sort.costDesc',
  'cost-asc': 'sort.costAsc',
  'energy-desc': 'sort.energyDesc',
  'energy-asc': 'sort.energyAsc',
  'duration-desc': 'sort.durationDesc',
  'duration-asc': 'sort.durationAsc',
  'location-asc': 'sort.locationAsc',
};

/** Comparator factory — returns a sort fn for the given mode.
 *  All comparators are pure on SessionData fields available
 *  client-side; no dependencies on API state. */
function sortComparator(mode: Sort): (a: SessionData, b: SessionData) => number {
  switch (mode) {
    case 'newest':
      return (a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime();
    case 'oldest':
      return (a, b) => new Date(a.started_at).getTime() - new Date(b.started_at).getTime();
    case 'cost-desc':
      return (a, b) => (b.cost?.total ?? 0) - (a.cost?.total ?? 0);
    case 'cost-asc':
      return (a, b) => (a.cost?.total ?? 0) - (b.cost?.total ?? 0);
    case 'energy-desc':
      return (a, b) => (b.energy_kwh ?? 0) - (a.energy_kwh ?? 0);
    case 'energy-asc':
      return (a, b) => (a.energy_kwh ?? 0) - (b.energy_kwh ?? 0);
    case 'duration-desc':
      return (a, b) => (b.duration_minutes ?? 0) - (a.duration_minutes ?? 0);
    case 'duration-asc':
      return (a, b) => (a.duration_minutes ?? 0) - (b.duration_minutes ?? 0);
    case 'location-asc':
      return (a, b) => (a.location_name ?? '').localeCompare(b.location_name ?? '');
  }
}

/**
 * Period preset for the dashboard's date-range selector.
 * Computed in the driver's local timezone — see periodToRange()
 * below for the conversion to ISO 8601 wire format.
 *
 * 'all' means no date filter (server endpoint accepts both
 * from + to omitted). 'custom' means the driver picked specific
 * dates via the native date inputs revealed when this preset
 * is selected.
 */
type Period = '7d' | '30d' | '90d' | '1y' | 'all' | 'custom';

// Landing default flipped from '30d' to '7d' alongside the revenue
// chart slot — a one-week view reads as the "rhythm" most drivers
// want at a glance; longer windows are one tap away via the period
// chip row.
const DEFAULT_PERIOD: Period = '7d';

/** Convert a Period preset (and optional custom from/to YYYY-MM-DD
 *  strings) into ISO 8601 wire values for the server endpoint.
 *  Custom inputs are interpreted in the driver's local timezone:
 *  start-of-day for from, end-of-day for to. Presets compute as
 *  rolling-back-from-now in physical time (timezone-irrelevant for
 *  this case — the same instant in any timezone). */
function periodToRange(
  period: Period,
  customFrom: string | null,
  customTo: string | null,
): { from: string | null; to: string | null } {
  if (period === 'all') return { from: null, to: null };
  if (period === 'custom') {
    if (!customFrom || !customTo) return { from: null, to: null };
    // Parse YYYY-MM-DD as local-tz dates so a driver in PST who
    // picks "2026-05-01" gets PST midnight, not UTC midnight (which
    // would be 4-7 hours earlier in their actual day).
    const [fy, fm, fd] = customFrom.split('-').map(Number);
    const [ty, tm, td] = customTo.split('-').map(Number);
    if (!fy || !fm || !fd || !ty || !tm || !td) return { from: null, to: null };
    const fromDate = new Date(fy, fm - 1, fd, 0, 0, 0, 0);
    const toDate = new Date(ty, tm - 1, td, 23, 59, 59, 999);
    return { from: fromDate.toISOString(), to: toDate.toISOString() };
  }
  // Preset: rolling N days back from now.
  const dayMs = 24 * 60 * 60 * 1000;
  const now = Date.now();
  const days = period === '7d' ? 7
    : period === '30d' ? 30
    : period === '90d' ? 90
    : 365; // '1y'
  return {
    from: new Date(now - days * dayMs).toISOString(),
    to: new Date(now).toISOString(),
  };
}

/** Maps each period preset to its i18n key (the long-form label
 *  used on the dashboard hero). Resolve via t(PERIOD_LABELS[p]). */
const PERIOD_LABELS: Record<Period, string> = {
  '7d': 'period.full7d',
  '30d': 'period.full30d',
  '90d': 'period.full90d',
  '1y': 'period.full1y',
  all: 'period.all',
  custom: 'period.custom',
};

/** Detect driver's local timezone for the cover-note copy on
 *  exports + display. Falls back to a hint string in SSR /
 *  unsupported envs (PWA always runs in-browser so this branch
 *  is defensive). */
function getDriverTz(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/**
 * Top-level tab on the History page. Sessions tab keeps the
 * tax-prep contract intact (dashboard widgets, CSV / PDF / ZIP
 * exports, multi-select bundle); Activity tab is the unified
 * timeline (sessions + wallet top-ups + points + coupons).
 *
 * URL-synced via ?tab=activity so refreshing or sharing a link
 * preserves the surface; default is 'sessions' to match the
 * historical entry point + the founder spec that anchors the
 * exports surface on charging-only.
 */
type Tab = 'sessions' | 'activity';
const DEFAULT_TAB: Tab = 'sessions';

function readTabFromUrl(fallback: Tab): Tab {
  if (typeof window === 'undefined') return fallback;
  const raw = new URLSearchParams(window.location.search).get('tab');
  return raw === 'sessions' || raw === 'activity' ? raw : fallback;
}
function writeTabToUrl(tab: Tab): void {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  if (tab === DEFAULT_TAB) params.delete('tab');
  else params.set('tab', tab);
  const search = params.toString();
  const next = `${window.location.pathname}${search ? `?${search}` : ''}`;
  window.history.replaceState({}, '', next);
}

/** URL-sync helpers — period selector state survives page refresh.
 *  Uses replaceState so chip taps don't pollute browser back-history;
 *  Read on mount via useState lazy initializer. */
function readPeriodFromUrl(fallback: Period): Period {
  if (typeof window === 'undefined') return fallback;
  const params = new URLSearchParams(window.location.search);
  const raw = params.get('period');
  if (raw === '7d' || raw === '30d' || raw === '90d' || raw === '1y'
    || raw === 'all' || raw === 'custom') {
    return raw;
  }
  return fallback;
}
function readCustomDateFromUrl(key: 'from' | 'to'): string | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const raw = params.get(key);
  // YYYY-MM-DD shape check — defensive against arbitrary URL params.
  return raw && /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : null;
}
function writePeriodToUrl(
  period: Period,
  customFrom: string | null,
  customTo: string | null,
): void {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  params.set('period', period);
  if (period === 'custom') {
    if (customFrom) params.set('from', customFrom); else params.delete('from');
    if (customTo) params.set('to', customTo); else params.delete('to');
  } else {
    params.delete('from');
    params.delete('to');
  }
  const next = `${window.location.pathname}?${params.toString()}`;
  window.history.replaceState({}, '', next);
}

/**
 * Driver session history.
 *
 * Reads real data from /sessions (replaced the mock-data shape
 * shipped pre-launch). Bottom-sheet detail surface stays —
 * mobile-native, matches the AvatarPicker / friend-detail
 * sheet pattern elsewhere in the PWA. The View Invoice button
 * in the sheet opens the Stripe-hosted receipt URL in a new
 * tab when one is on file (denormalized from Payout via the
 * payment_intent.succeeded webhook).
 *
 * Filter mapping (client-side, not API-side)
 * ──────────────────────────────────────────
 * The API's /sessions endpoint accepts a single status string;
 * the driver-facing "Completed" filter spans three terminal
 * statuses (completed / stopped_by_user / stopped_by_system —
 * the same COMPLETED_STATUSES set the dashboard widgets use).
 * "Cancelled" maps to status='failed'. Filtering server-side
 * with a single status would mis-classify the stopped variants
 * as not-completed, so we fetch everything (limit-bounded) and
 * filter in memory — cheap on the client, accurate.
 */

/** Statuses that count as "this session delivered energy and
 *  the driver was billed for it." Mirrors the dashboard's
 *  COMPLETED_STATUSES set so two surfaces agree on
 *  "completed." */
const COMPLETED_STATUSES = new Set<string>([
  'completed', 'stopped_by_user', 'stopped_by_system',
]);

function formatDateLabel(iso: string, t: ReturnType<typeof useTranslation>['t']): string {
  const d = new Date(iso);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
  const target = new Date(d); target.setHours(0, 0, 0, 0);
  if (target.getTime() === today.getTime()) return t('date.today');
  if (target.getTime() === yesterday.getTime()) return t('date.yesterday');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-US', {
    hour: 'numeric', minute: '2-digit',
  });
}

function formatDurationMinutes(min: number): string {
  if (min <= 0) return '0m';
  const hours = Math.floor(min / 60);
  const remMinutes = Math.round(min % 60);
  return hours > 0 ? `${hours}:${String(remMinutes).padStart(2, '0')} hrs` : `${remMinutes}m`;
}

/** Spec cap from the server-side bundle endpoint. The selection
 *  UI exposes the cap so a driver can't queue a 200-row bundle
 *  and only learn it failed after tapping Download. Mirrors
 *  MAX_SESSIONS_PER_BUNDLE in apps/api/src/routes/me/sessions-
 *  receipts-bundle.ts. */
const RECEIPTS_BUNDLE_CAP = 100;

export function HistoryPage({ onNavigate }: Props) {
  const { t } = useTranslation('history');
  const [filter, setFilter] = useState<Filter>('all');
  const [sort, setSort] = useState<Sort>(DEFAULT_SORT);
  const [sortSheetOpen, setSortSheetOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);

  // ── Top-level tab state ──────────────────────────────────────
  const [tab, setTab] = useState<Tab>(() => readTabFromUrl(DEFAULT_TAB));
  useEffect(() => {
    writeTabToUrl(tab);
  }, [tab]);

  // Fleet-mode gate on the Activity tab. Fleet drivers don't earn
  // individual points or rewards (Phase 5 gamification gate fires
  // when session.mode_used === 'fleet'), so the unified activity
  // feed has nothing to show in fleet mode — every event type the
  // feed renders (sessions, wallet top-ups, points, coupons) is
  // either suppressed or attributed to the fleet rather than the
  // driver. Hiding the tab prevents a dead-end navigation.
  //
  // When the driver flips to fleet mode while sitting on the
  // Activity tab, the effect below redirects them to Sessions.
  // The writeTabToUrl effect above then strips ?tab=activity from
  // the URL on the same tick.
  const { mode: currentMode } = useCurrentMode();
  const activityTabVisible = currentMode !== 'fleet';
  useEffect(() => {
    if (!activityTabVisible && tab === 'activity') {
      setTab('sessions');
    }
  }, [activityTabVisible, tab]);

  // Activity-tab state lifted to the parent so it survives tab
  // switches. The user picks "Coupons only" on Activity, flips
  // to Sessions, flips back — the chip state has to be exactly
  // where they left it. ActivityTab itself is unmounted while
  // hidden (we don't pay render cost on the wrong tab), so the
  // state has to live at a stabler scope than ActivityTab.
  const [activitySelectedTypes, setActivitySelectedTypes] = useState<Set<ActivityType>>(
    () => new Set<ActivityType>(['session', 'wallet_topup', 'points', 'coupon']),
  );

  // ── Multi-select state for the receipts ZIP bundle ───────────
  // selectMode toggles the row chrome from "tap-to-open-detail"
  // to "tap-to-toggle-selection" (Gmail pattern). The selected
  // ids live in a Set for O(1) membership lookups during render.
  // bundleBusy gates the action bar's Download button while the
  // server is fetching + ZIPping receipts.
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [bundleBusy, setBundleBusy] = useState(false);
  const exitSelectMode = (): void => {
    setSelectMode(false);
    setSelectedIds(new Set());
  };
  const toggleSelected = (id: string): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else if (next.size < RECEIPTS_BUNDLE_CAP) {
        next.add(id);
      } else {
        toast(t('toast.selectCap', { cap: RECEIPTS_BUNDLE_CAP }), {
          tone: 'warn', durationMs: 2400,
        });
        return prev;
      }
      return next;
    });
  };

  // ── Period selector state ─────────────────────────────────────
  // URL-synced so a refresh preserves the driver's selection. The
  // search-params write path uses replaceState rather than pushState
  // so chip taps don't pollute browser back-history with one entry
  // per period change.
  const [period, setPeriod] = useState<Period>(() => readPeriodFromUrl(DEFAULT_PERIOD));
  const [customFrom, setCustomFrom] = useState<string | null>(
    () => readCustomDateFromUrl('from'),
  );
  const [customTo, setCustomTo] = useState<string | null>(
    () => readCustomDateFromUrl('to'),
  );

  useEffect(() => {
    writePeriodToUrl(period, customFrom, customTo);
  }, [period, customFrom, customTo]);

  const range = useMemo(
    () => periodToRange(period, customFrom, customTo),
    [period, customFrom, customTo],
  );

  // Paginated session list. First page lands on mount with 5
  // rows visible; tapping "Load more" appends another 5. The
  // small initial page keeps the History page scannable above
  // the fold — drivers who want deeper history get it via
  // explicit Load More taps rather than a long scroll on first
  // paint. accumulatedSessions is the rendered buffer;
  // meta.total drives the Load More gate.
  const PAGE_SIZE = 5;
  const [sessionsPage, setSessionsPage] = useState(1);
  const sessionsQ = useQuery({
    queryKey: ['me', 'sessions', sessionsPage, PAGE_SIZE],
    queryFn: () => fetchMySessions({ page: sessionsPage, limit: PAGE_SIZE }),
    staleTime: 30_000,
  });
  // Append-on-paginate buffer. Resets on a fresh queryKey
  // (e.g. when sessionsPage falls back to 1 via the
  // refresh-and-reset path). The accumulated buffer is the
  // source of truth for what's rendered + what filtered/
  // sorted operates on.
  const [accumulatedSessions, setAccumulatedSessions] = useState<SessionData[]>([]);
  const [totalSessionCount, setTotalSessionCount] = useState(0);
  useEffect(() => {
    if (!sessionsQ.data) return;
    setTotalSessionCount(sessionsQ.data.meta.total);
    if (sessionsPage === 1) {
      // Fresh first page — replace whatever buffer was there
      // (covers refetch-on-mount and explicit reset paths).
      setAccumulatedSessions(sessionsQ.data.data);
    } else {
      // Append. De-dupe by _id in case React Query returns
      // overlapping pages (rare but defensive — Mongo's skip
      // can repeat rows if a new session lands mid-pagination).
      setAccumulatedSessions((prev) => {
        const seen = new Set(prev.map((s) => s._id));
        const next = [...prev];
        for (const s of sessionsQ.data!.data) {
          if (!seen.has(s._id)) next.push(s);
        }
        return next;
      });
    }
  }, [sessionsQ.data, sessionsPage]);
  const canLoadMore = accumulatedSessions.length < totalSessionCount;

  // ── Dashboard summary query ───────────────────────────────────
  // Cache key includes (from, to) so period changes refetch cleanly
  // and back-and-forth between presets reads from cache (React Query
  // staleTime of 30s).
  const summaryQ = useQuery({
    queryKey: ['me', 'charging', 'summary', range.from, range.to],
    queryFn: () => fetchChargingSummary({ from: range.from, to: range.to }),
    staleTime: 30_000,
  });

  // ── Revenue-series query (feeds the chart slot above the stat
  //    grid). Every period the page offers renders a chart now: the
  //    route buckets any window by span (daily / weekly / monthly),
  //    and the page's already-computed range rides along so custom
  //    ranges work identically. Cache key covers period AND range so
  //    chip taps and custom edits refetch cleanly. ─────────────────
  const revenueSeriesQ = useQuery({
    queryKey: ['me', 'charging', 'revenue-series', period, range.from, range.to],
    queryFn: () => fetchMyChargingRevenueSeries(period, {
      from: range.from ?? undefined,
      to: range.to ?? undefined,
    }),
    staleTime: 30_000,
  });

  // Zero-session driver detection — drives the empty hero in
  // place of the dashboard / filter pills / list. Defer the
  // judgment until the sessions query lands so we don't flash
  // the empty hero during the initial load.
  // hasAnySessions still drives the inline "Show all time" hint
  // inside the dashboard when the period happens to have zero
  // sessions but the driver does have charging history. The
  // empty-driver hero from earlier phases is gone — widgets
  // always render with zeros inline when there's no data, which
  // is more honest UX than swapping the page surface entirely.
  const hasAnySessions = sessionsQ.data ? totalSessionCount > 0 : false;

  const filtered = useMemo(() => {
    const all = accumulatedSessions;
    let next: SessionData[];
    if (filter === 'all') {
      next = all;
    } else if (filter === 'completed') {
      next = all.filter((s) => COMPLETED_STATUSES.has(s.status));
    } else {
      // 'cancelled' bucket — failed only. The 'pending' / 'sweeping'
      // states are transient and don't survive long enough to
      // appear in driver history; 'active' is filtered out as a
      // visible-only-on-the-Active-Session UI elsewhere.
      next = all.filter((s) => s.status === 'failed');
    }
    // Date-range slice — the period chips + Custom date pickers
    // previously narrowed only the dashboard widgets above (via
    // summaryQ keyed on range.from/to). The session list below
    // ignored the range entirely, which read as a bug to drivers
    // who expected the chips to filter the visible rows too.
    // Slice on started_at: a session whose start is inside the
    // window stays visible even if it ended outside it (a session
    // straddling midnight on the boundary day shouldn't disappear).
    // 'All time' has range.from=null + range.to=null and naturally
    // short-circuits this block. Client-side slice works against
    // the loaded buffer (page-by-page from fetchMySessions); deep-
    // history drivers wanting older data must Load More to expand
    // the buffer first. Server-side date filter on the list query
    // is the Phase 2 follow-up.
    if (range.from || range.to) {
      const fromMs = range.from ? new Date(range.from).getTime() : -Infinity;
      const toMs = range.to ? new Date(range.to).getTime() : Infinity;
      next = next.filter((s) => {
        const startMs = new Date(s.started_at).getTime();
        return startMs >= fromMs && startMs <= toMs;
      });
    }
    // Sort within the filtered set. Spread to avoid mutating the
    // React Query cache slice (sort() mutates in place).
    if (sort !== DEFAULT_SORT) {
      next = [...next].sort(sortComparator(sort));
    } else if (next !== all) {
      // 'newest' is the server's default order — only spread when
      // we filtered, otherwise the caller gets the raw cache slice.
      // (Keeps the array identity stable for non-mutating renders.)
    }
    return next;
  }, [accumulatedSessions, filter, sort, range.from, range.to]);

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <button
          type="button"
          className={styles.backFab}
          aria-label={t('a11y.back')}
          onClick={() => onNavigate('/profile')}
        >
          <ArrowLeft size={16} />
        </button>
        <div className={styles.titleBlock}>
          <h1 className={styles.title}>{t('title')}</h1>
          <p className={styles.subtitle}>
            {/* Subtitle is tab-specific: Sessions surfaces the
                charging count + buffered/total split for Load
                More; Activity surfaces the unified-feed framing.
                Both use the same N-loaded vs T-total math when
                applicable. */}
            {tab === 'sessions'
              ? (sessionsQ.isLoading && accumulatedSessions.length === 0
                  ? t('common.loading')
                  : accumulatedSessions.length < totalSessionCount && filter === 'all'
                    ? t('subtitle.ofTotal', { loaded: accumulatedSessions.length, total: totalSessionCount })
                    : t('subtitle.count', { count: filtered.length }))
              : t('subtitle.allTransactions')}
          </p>
        </div>
      </header>

      {/* Non-driver hint banner — invisible for production drivers
          (the production case), visible for ops / dev sessions
          where an admin role is testing the PWA. Heads off the
          "widgets are zero, must be a bug" diagnostic loop that
          cost real time twice in a single session. */}
      <NonDriverBanner />

      {/* Tab switcher — Sessions / Activity. Sessions tab keeps
          the existing tax-prep surface (dashboard, exports,
          ZIP); Activity tab is the unified timeline. Pill
          segmentation matches the filter pills below for visual
          consistency. */}
      <div className={styles.tabRow} role="tablist" aria-label={t('a11y.historyView')}>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'sessions'}
          className={`${styles.tabBtn} ${tab === 'sessions' ? styles.tabBtnActive : ''}`}
          onClick={() => setTab('sessions')}
        >
          {t('tab.sessions')}
        </button>
        {activityTabVisible && (
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'activity'}
            className={`${styles.tabBtn} ${tab === 'activity' ? styles.tabBtnActive : ''}`}
            onClick={() => setTab('activity')}
          >
            {t('tab.activity')}
          </button>
        )}
      </div>

      {/* Activity tab: unified feed. Renders alongside the page
          header + tab switcher; doesn't share Sessions tab's
          dashboard or list state. */}
      {tab === 'activity' && (
        <ActivityTab
          range={range}
          period={period}
          customFrom={customFrom}
          customTo={customTo}
          setPeriod={setPeriod}
          setCustomFrom={setCustomFrom}
          setCustomTo={setCustomTo}
          onOpenSession={(sessionId) => setDetailId(sessionId)}
          selectedTypes={activitySelectedTypes}
          setSelectedTypes={setActivitySelectedTypes}
          hasAnySessions={hasAnySessions}
        />
      )}

      {/* Sessions tab: dashboard widgets ALWAYS render, regardless
          of session count. Zero values surface inline ($0.00, 0
          kWh, 0 sessions) — the empty-driver hero from earlier
          phases is gone. Empty list area below the widgets gets
          its own inline message. */}
      {tab === 'sessions' && (
        <>
          {/* ── Period selector ─────────────────────────────── */}
          <div className={styles.periodSelector} role="tablist" aria-label={t('a11y.dateRange')}>
            <PeriodChip label={t('period.d7')} value="7d" active={period === '7d'} onClick={setPeriod} />
            <PeriodChip label={t('period.d30')} value="30d" active={period === '30d'} onClick={setPeriod} />
            <PeriodChip label={t('period.d90')} value="90d" active={period === '90d'} onClick={setPeriod} />
            <PeriodChip label={t('period.y1')} value="1y" active={period === '1y'} onClick={setPeriod} />
            <PeriodChip label={t('period.all')} value="all" active={period === 'all'} onClick={setPeriod} />
            <PeriodChip label={t('period.custom')} value="custom" active={period === 'custom'} onClick={setPeriod} />
          </div>
          {period === 'custom' && (
            <div className={styles.customRow}>
              <input
                type="date"
                aria-label={t('a11y.fromDate')}
                className={styles.customInput}
                value={customFrom ?? ''}
                onChange={(e) => setCustomFrom(e.target.value || null)}
              />
              <span className={styles.customSep}>→</span>
              <input
                type="date"
                aria-label={t('a11y.toDate')}
                className={styles.customInput}
                value={customTo ?? ''}
                onChange={(e) => setCustomTo(e.target.value || null)}
              />
            </div>
          )}

          {/* ── Dashboard ───────────────────────────────────── */}
          <DashboardSection
            summaryQ={summaryQ}
            period={period}
            range={range}
            hasAnySessions={hasAnySessions}
            onSwitchToAllTime={() => setPeriod('all')}
            revenueSeriesData={revenueSeriesQ.data}
            onPeriodChange={setPeriod}
          />

          {/* Filter pills + sort dropdown + select toggle row.
              Filters on the left; sort + select on the right.
              The plan specifies a dropdown for filter too, but
              small pill segmentation is cheaper on thumb reach
              at 320px widths. */}
          <div className={styles.filterRow}>
            <div className={styles.filters} role="tablist" aria-label={t('a11y.filterSessions')}>
              <FilterPill label={t('filter.all')} value="all" active={filter === 'all'} onClick={setFilter} />
              <FilterPill label={t('filter.completed')} value="completed" active={filter === 'completed'} onClick={setFilter} />
              <FilterPill label={t('filter.cancelled')} value="cancelled" active={filter === 'cancelled'} onClick={setFilter} />
            </div>
            <div className={styles.filterRowActions}>
              <button
                type="button"
                className={styles.sortButton}
                onClick={() => setSortSheetOpen(true)}
                aria-label={t('a11y.sort', { label: t(SORT_LABELS[sort]) })}
              >
                <span className={styles.sortLabel}>{t(SORT_LABELS[sort])}</span>
                <ChevronDown size={14} aria-hidden />
              </button>
              <button
                type="button"
                className={`${styles.selectToggle} ${selectMode ? styles.selectToggleActive : ''}`}
                onClick={() => (selectMode ? exitSelectMode() : setSelectMode(true))}
                aria-pressed={selectMode}
                aria-label={selectMode ? t('a11y.exitSelect') : t('a11y.selectForBundle')}
              >
                {selectMode ? t('select.done') : t('select.select')}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Sort selection bottom sheet — uses the existing primitive
          so the chrome (slide-up animation, focus trap, ESC) matches
          the Profile submenus. */}
      <BottomSheet
        isOpen={sortSheetOpen}
        onClose={() => setSortSheetOpen(false)}
        title={t('sortSheet.title')}
        glass
      >
        <div className={styles.sortOptionList}>
          {(Object.keys(SORT_LABELS) as Sort[]).map((opt) => (
            <button
              key={opt}
              type="button"
              className={`${styles.sortOption} ${opt === sort ? styles.sortOptionActive : ''}`}
              onClick={() => {
                setSort(opt);
                setSortSheetOpen(false);
              }}
            >
              {t(SORT_LABELS[opt])}
            </button>
          ))}
        </div>
      </BottomSheet>

      {tab === 'sessions' && (
        <div className={styles.list}>
          {sessionsQ.isLoading ? (
            <p className={styles.empty}>
              <Loader2 size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              {t('list.loading')}
            </p>
          ) : sessionsQ.isError ? (
            <p className={styles.empty}>{t('list.loadError')}</p>
          ) : filtered.length === 0 ? (
            // Empty-period: driver has sessions overall but none
            // match the current filter (status pill or period).
            // The dashboard above already shows zero values which
            // carry the "this period is empty" signal — this copy
            // stays small + offers a one-tap escape to All time
            // when the driver actually has charging history that
            // 'all' would surface. Hidden on filter='completed' /
            // 'cancelled' since those exclude rows the period
            // does include — would be a misleading CTA.
            <p className={styles.empty}>
              {filter === 'all' ? t('list.emptyPeriod') : t('list.emptyFilter')}
              {period !== 'all' && filter === 'all' && hasAnySessions && (
                <>
                  {' '}
                  <button
                    type="button"
                    className={styles.emptyLink}
                    onClick={() => setPeriod('all')}
                  >
                    {t('common.showAllTime')}
                  </button>
                </>
              )}
            </p>
          ) : (
            <>
              {filtered.map((s) => (
                <SessionRow
                  key={s._id}
                  session={s}
                  onOpen={() => setDetailId(s._id)}
                  selectMode={selectMode}
                  selected={selectedIds.has(s._id)}
                  onToggle={() => toggleSelected(s._id)}
                />
              ))}
              {/* Load More — appears when the buffered count is
                  short of the server-reported total. Disabled
                  while the next page is in flight. Hidden while
                  a non-default status filter is active so the
                  button doesn't read as "load more cancelled
                  sessions" when the next page might also be
                  mostly completed (status filter is client-
                  side; loading more loads ALL statuses). */}
              {canLoadMore && filter === 'all' && (
                <button
                  type="button"
                  className={styles.loadMoreBtn}
                  disabled={sessionsQ.isFetching}
                  onClick={() => setSessionsPage((p) => p + 1)}
                >
                  {sessionsQ.isFetching ? (
                    <>
                      <Loader2 size={14} className={styles.exportSpin} aria-hidden />
                      {t('common.loading')}
                    </>
                  ) : (
                    <>
                      {t('common.loadMore')}
                      <span className={styles.loadMoreCount}>
                        {t('common.remaining', { n: totalSessionCount - accumulatedSessions.length })}
                      </span>
                    </>
                  )}
                </button>
              )}
            </>
          )}
        </div>
      )}

      {/* Bottom action bar — visible only in select mode. Floats
          above the tab bar (z-index higher than the floating nav
          but below the detail-sheet backdrop) so the driver can
          always see the count + download CTA without scrolling. */}
      {selectMode && (
        <ReceiptsBundleActionBar
          count={selectedIds.size}
          busy={bundleBusy}
          onCancel={exitSelectMode}
          onDownload={async () => {
            if (bundleBusy || selectedIds.size === 0) return;
            setBundleBusy(true);
            try {
              const ids = Array.from(selectedIds);
              const { missing } = await downloadReceiptsBundle(ids);
              if (missing.length === 0) {
                toast(t('toast.downloaded', { count: ids.length }), { tone: 'accent' });
              } else {
                // Mixed-result framing: ZIP succeeded but N rows
                // didn't have a fetchable receipt. The driver still
                // got the bundle (with its manifest) and we tell
                // them what's missing so they can decide whether
                // to try again later or skip those sessions.
                const downloaded = ids.length - missing.length;
                toast(
                  t('toast.downloadedPartial', { downloaded, total: ids.length, missing: missing.length }),
                  { tone: 'warn', durationMs: 4500 },
                );
              }
              exitSelectMode();
            } catch (err) {
              const msg = err instanceof Error ? err.message : t('toast.downloadReceiptsError');
              toast(msg, { tone: 'error', durationMs: 3500 });
            } finally {
              setBundleBusy(false);
            }
          }}
        />
      )}

      {detailId && (
        <DetailSheet sessionId={detailId} onClose={() => setDetailId(null)} />
      )}
    </div>
  );
}

function FilterPill({
  label, value, active, onClick,
}: {
  label: string;
  value: Filter;
  active: boolean;
  onClick: (v: Filter) => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      className={`${styles.filterPill} ${active ? styles.filterPillActive : ''}`}
      onClick={() => onClick(value)}
    >
      {label}
    </button>
  );
}

function PeriodChip({
  label, value, active, onClick,
}: {
  label: string;
  value: Period;
  active: boolean;
  onClick: (v: Period) => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      className={`${styles.periodChip} ${active ? styles.periodChipActive : ''}`}
      onClick={() => onClick(value)}
    >
      {label}
    </button>
  );
}

/**
 * Dashboard widgets section — hero card with total $ + stat
 * grid (kWh, sessions, avg cost, avg duration, cost/kWh) + a
 * placeholder for the top-locations widget that ships in
 * commit 3.
 *
 * Reads from the summary query passed in by the parent.
 * Skeleton state on first load so the layout doesn't shift
 * when data lands. Empty-period state (sessionCount === 0)
 * still renders the cards with zero values — the period
 * chip + filter pills make it clear nothing's wrong.
 */
function DashboardSection({
  summaryQ,
  period,
  range,
  hasAnySessions,
  onSwitchToAllTime,
  revenueSeriesData,
  onPeriodChange,
}: {
  summaryQ: { data?: ChargingSummary; isLoading: boolean; isError: boolean };
  period: Period;
  range: { from: string | null; to: string | null };
  /** True when sessionsQ has at least one session for the driver
   *  (driver has charging history overall). Drives the empty-period
   *  hint below — distinguishes "this period has no sessions" from
   *  "this driver has never charged". */
  hasAnySessions: boolean;
  /** Callback to flip the period to 'all' when the inline hint's
   *  "Show all time" link is tapped. */
  onSwitchToAllTime: () => void;
  /** Driver revenue series for the chart slot. Undefined while
   *  loading; absent on broader periods where the chart's hidden. */
  revenueSeriesData: MyChargingRevenueSeries | undefined;
  /** Promote period changes from the chart's chip row up to
   *  HistoryPage's canonical period state. The page's existing
   *  period selector still drives the broader periods (90d / 1y /
   *  all / custom); the chart's chip is a secondary control that
   *  mirrors the same state for 7d / 30d. */
  onPeriodChange: (next: Period) => void;
}) {
  const { t } = useTranslation('history');
  if (summaryQ.isLoading && !summaryQ.data) {
    return (
      <section className={styles.dashboard} aria-label={t('a11y.chargingSummary')}>
        <div className={`${styles.heroCard} ${styles.skeleton}`} aria-hidden />
        <div className={styles.statGrid}>
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className={`${styles.statCard} ${styles.skeleton}`} aria-hidden />
          ))}
        </div>
      </section>
    );
  }

  if (summaryQ.isError) {
    return (
      <section className={styles.dashboard} aria-label={t('a11y.chargingSummary')}>
        <p className={styles.dashboardError}>
          {t('dashboard.summaryError')}
        </p>
      </section>
    );
  }

  const s = summaryQ.data;
  if (!s) return null;

  const periodLabel = t(PERIOD_LABELS[period]);
  // Disable export buttons while sessionCount is 0 — the CSV
  // would be header-only, which is technically valid but reads
  // as a "did this work?" surprise. Belt + suspenders: server
  // already handles the empty case (returns header-only CSV),
  // we just keep the UX clear.
  const canExport = s.sessionCount > 0;
  // "I have sessions but not in this period" hint. Surfaces
  // when the date-windowed summary is empty BUT the driver's
  // overall sessions list isn't. Catches three real-world
  // cases without forcing the driver to think about which one
  // they're in:
  //   1. Driver hasn't charged in the last <period> days
  //      (vacation, EV in shop, work-from-home stretch).
  //   2. Test-fixture data with started_at outside the
  //      rolling-from-now window (common in dev environments
  //      where seed dates drift from server clock).
  //   3. Any other scoping/window quirk that produces a
  //      zero-aggregate but non-zero list.
  // Switching to 'all' time always resolves all three.
  const periodEmptyButHasHistory =
    s.sessionCount === 0 && hasAnySessions && period !== 'all';

  return (
    <section className={styles.dashboard} aria-label={t('a11y.chargingSummary')}>
      {/* Hero — total \$ in period. RewardsPage hero treatment:
          surface chrome + glow breathe + 48px hero number. */}
      <div className={styles.heroCard}>
        {/* Period label only. The driver's IANA tz used to print
            here too but reads as eng-debug noise on a polished
            hero — drivers don't need to confirm their own zone.
            getDriverTz() still flows through to the PDF cover
            note where the explicit zone IS useful for accountants
            opening the file from anywhere. */}
        <span className={styles.heroEyebrow}>{periodLabel}</span>
        <div className={styles.heroValueRow}>
          <span className={styles.heroValue}>
            {formatCents(s.totalCents)}
          </span>
        </div>
        <span className={styles.heroSubtitle}>
          {t('dashboard.totalSpent', { count: s.sessionCount, formatted: s.sessionCount.toLocaleString() })}
        </span>
      </div>

      {/* Empty-period hint — fires when the driver has sessions
          overall but none in the current period window. One-tap
          "Show all time" promotes the period selector to the all-
          time preset, which always lights the dashboard up. */}
      {periodEmptyButHasHistory && (
        <div className={styles.periodEmptyHint} role="status">
          <span className={styles.periodEmptyHintText}>
            {t('dashboard.emptyPeriodHint')}
          </span>
          <button
            type="button"
            className={styles.periodEmptyHintLink}
            onClick={onSwitchToAllTime}
          >
            {t('common.showAllTime')}
          </button>
        </div>
      )}

      {/* Export action row. CSV ships in commit 4; PDF joins in
          commit 5. Buttons disable when the period has no sessions
          to keep the empty-CSV-surprise off the table. */}
      <ExportActions range={range} canExport={canExport} />

      {/* Revenue chart — driver-scoped time series for the active
          period. Renders for EVERY period the page offers: the
          route buckets any window by span (daily / weekly /
          monthly) and takes the page's computed range, so 90d, 1y,
          all, and custom chart like 7d/30d always did. The chart's
          internal chip row covers the three named windows drivers
          tap most; 1y/all/custom stay page-chip-driven (the chart
          renders them with no internal chip highlighted, per its
          contract). showRate=false because drivers don't read
          trailing $/hr — the footer slot carries a driver-relevant
          total + session count via PeriodTotalPill instead. */}
      {revenueSeriesData && (
        <div className={styles.revenueChartWrap}>
          <RevenueChart
            data={revenueSeriesData.buckets}
            currentRate={0}
            period={period}
            allowedPeriods={['7d', '30d', '90d']}
            onPeriodChange={(next) => onPeriodChange(next as Period)}
            showRate={false}
            footer={
              <RevenuePeriodPill
                label={t('dashboard.revenueTotal')}
                amount={revenueSeriesData.totalSpent}
                sessionCount={revenueSeriesData.sessionCount}
              />
            }
          />
        </div>
      )}

      {/* Stat grid — five small cards. Order chosen so the
          most-glanceable numbers (kWh + sessions) sit at the
          top, with the averages + tax-prep cost/kWh below. */}
      <div className={styles.statGrid}>
        <StatCard label={t('dashboard.statTotalKwh')} value={`${s.totalKwh.toFixed(1)}`} />
        <StatCard label={t('dashboard.statSessions')} value={`${s.sessionCount.toLocaleString()}`} />
        <StatCard label={t('dashboard.statAvgCost')} value={formatCents(s.avgCostCents)} />
        <StatCard label={t('dashboard.statAvgDuration')} value={formatMinutes(s.avgDurationMinutes)} />
        <StatCard
          label={t('dashboard.statCostPerKwh')}
          value={s.totalKwh > 0 ? formatCents(s.costPerKwhCents) : '—'}
        />
      </div>

      {/* Top locations — by kWh delivered (locked metric per
          ticket). Always renders; the widget surfaces its own
          inline empty state when locations.length === 0 so the
          Sessions tab dashboard's vertical rhythm stays
          consistent across populated + zero-data periods. */}
      <TopLocationsWidget locations={s.topLocations} />
    </section>
  );
}

/**
 * Top-N locations by kWh delivered. Each row shows the location
 * name + a relative bar (% width = location.kwh / max), session
 * count, and total kWh. The bar's relative scaling makes the
 * #1 location reach 100% width — drivers see the rank order as
 * a visual gradient even before reading the numbers.
 *
 * Locked metric: kWh (per ticket). Session-count and spend are
 * polish triggers if drivers ask.
 */
function TopLocationsWidget({
  locations,
}: {
  locations: ChargingSummary['topLocations'];
}) {
  const { t } = useTranslation('history');
  // Defensive: max() on an empty array returns -Infinity. The
  // floor of 0.1 keeps the bar math safe across the empty case
  // + any future caller that forgets to gate the array.
  const maxKwh = Math.max(0.1, ...locations.map((l) => l.kwh));

  // Empty state lives INSIDE the widget card rather than
  // gating the widget itself. Keeps the dashboard's vertical
  // rhythm consistent across populated and zero-data periods —
  // the Sessions tab always shows the same widget stack.
  if (locations.length === 0) {
    return (
      <div className={styles.topLocations}>
        <h3 className={styles.topLocationsTitle}>{t('topLocations.title')}</h3>
        <p className={styles.topLocationsEmpty}>{t('topLocations.empty')}</p>
      </div>
    );
  }

  return (
    <div className={styles.topLocations}>
      <h3 className={styles.topLocationsTitle}>{t('topLocations.title')}</h3>
      <ul className={styles.topLocationsList}>
        {locations.map((loc) => {
          const widthPct = Math.max(8, Math.round((loc.kwh / maxKwh) * 100));
          return (
            <li key={loc.name} className={styles.topLocationsRow}>
              <div className={styles.topLocationsRowHeader}>
                <span className={styles.topLocationsName}>
                  <MapPin size={12} aria-hidden />
                  {loc.name}
                </span>
                <span className={styles.topLocationsKwh}>
                  {t('topLocations.rowMeta', { kwh: loc.kwh.toFixed(1), count: loc.sessionCount })}
                </span>
              </div>
              <div className={styles.topLocationsBarTrack} aria-hidden>
                <div
                  className={styles.topLocationsBarFill}
                  style={{ width: `${widthPct}%` }}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * Export actions row — sits below the hero card. Wraps the shared
 * @evi/ui ExportActions component with the PWA's async-blob
 * download helpers (downloadSessionsCsv / downloadSessionsReportPdf)
 * + toast feedback. The shared component handles the buttons +
 * spinners + layout; this wrapper owns the lifecycle.
 */
function ExportActions({
  range,
  canExport,
}: {
  range: { from: string | null; to: string | null };
  canExport: boolean;
}) {
  const { t } = useTranslation('history');
  const [csvBusy, setCsvBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  const handleCsvClick = async () => {
    if (csvBusy || !canExport) return;
    setCsvBusy(true);
    try {
      await downloadSessionsCsv({ from: range.from, to: range.to });
      toast(t('export.csvDownloaded'), { tone: 'accent' });
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('export.csvError');
      toast(msg, { tone: 'error', durationMs: 3500 });
    } finally {
      setCsvBusy(false);
    }
  };

  const handlePdfClick = async () => {
    if (pdfBusy || !canExport) return;
    setPdfBusy(true);
    try {
      // Pass the driver's IANA tz so the cover note can name it
      // explicitly. Detected client-side via Intl rather than
      // server-derived (server has no reliable signal beyond IP
      // geo, which is wrong for VPN / travelling drivers).
      await downloadSessionsReportPdf({
        from: range.from,
        to: range.to,
        tz: getDriverTz(),
      });
      toast(t('export.pdfDownloaded'), { tone: 'accent' });
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('export.pdfError');
      toast(msg, { tone: 'error', durationMs: 3500 });
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <SharedExportActions
      onCsv={handleCsvClick}
      onPdf={handlePdfClick}
      csvBusy={csvBusy}
      pdfBusy={pdfBusy}
      disabled={!canExport}
    />
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.statCard}>
      <span className={styles.statLabel}>{label}</span>
      <span className={styles.statValue}>{value}</span>
    </div>
  );
}

function formatMinutes(min: number): string {
  if (min <= 0) return '0m';
  if (min < 60) return `${Math.round(min)}m`;
  const hours = Math.floor(min / 60);
  const remMinutes = Math.round(min % 60);
  return remMinutes > 0 ? `${hours}h ${remMinutes}m` : `${hours}h`;
}

function SessionRow({
  session, onOpen, selectMode, selected, onToggle,
}: {
  session: SessionData;
  onOpen: () => void;
  selectMode: boolean;
  selected: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation('history');
  const isCompleted = COMPLETED_STATUSES.has(session.status);
  const isCancelled = session.status === 'failed';
  const tone = isCompleted ? 'good' : isCancelled ? 'warn' : 'mute';
  const fallbackKey = statusLabelKey(session.status);
  const label = isCompleted
    ? t('status.completed')
    : isCancelled
      ? t('status.canceled')
      : (fallbackKey ? t(fallbackKey) : session.status);
  const totalCents = Math.round(session.cost?.total ?? 0);
  // In select mode the row's primary action becomes "toggle
  // selection" rather than "open detail sheet". Gmail pattern.
  // The whole row stays a single click target — easier on thumb
  // reach than a separate checkbox tap target.
  const handleClick = (): void => {
    if (selectMode) onToggle();
    else onOpen();
  };

  return (
    <button
      type="button"
      className={`${styles.row} ${selectMode ? styles.rowSelectMode : ''} ${selected ? styles.rowSelected : ''}`}
      onClick={handleClick}
      aria-pressed={selectMode ? selected : undefined}
    >
      {selectMode ? (
        <SelectionCheckbox selected={selected} />
      ) : (
        <DateChip status={session.status} />
      )}
      <div className={styles.rowInfo}>
        <p className={styles.rowDate}>{formatDateLabel(session.started_at, t)}</p>
        <p className={styles.rowMeta}>
          {formatTime(session.started_at)}
          {session.charger_identity ? ` · ${session.charger_identity}` : ''}
        </p>
      </div>
      <div className={styles.rowRight}>
        <StatusPill tone={tone} variant="soft" size="sm">{label}</StatusPill>
        <span className={styles.rowAmount}>
          {sessionNeverStartedNoCharge(session)
            ? t('detail.noCharge')
            : isCompleted && totalCents > 0
            ? formatCents(totalCents, session.cost?.currency ?? 'USD')
            : '—'}
        </span>
        <WalletFxSub session={session} />
      </div>
    </button>
  );
}

/** "US$7.14 from your wallet" under the CAD amount of a session a USD
 *  wallet paid at the Bank of Canada rate pinned at start (Warren,
 *  2026-10-25T02:52:41Z, option 1), so a history row shows both amounts. */
function WalletFxSub({ session }: { session: SessionData }) {
  const { t, i18n } = useTranslation('history');
  const fx = sessionWalletFxLine(session);
  if (!fx) return null;
  return (
    <span className={styles.rowAmountSub} data-wallet-fx={fx.rate}>
      {t('detail.walletFx', { amount: formatMoneyExplicit(fx.usdCents, 'USD', i18n.language) })}
    </span>
  );
}

/** Visual checkbox replacing the date chip in select mode. The
 *  underlying interaction is on the parent row's onClick — this
 *  is purely a state indicator. aria-hidden because the parent
 *  button already exposes aria-pressed for assistive tech. */
function SelectionCheckbox({ selected }: { selected: boolean }) {
  return (
    <span
      className={`${styles.selectionCheckbox} ${selected ? styles.selectionCheckboxOn : ''}`}
      aria-hidden
    >
      {selected && <Check size={16} />}
    </span>
  );
}

/** Sticky action bar shown while select mode is active. Sits
 *  above the floating tab bar (the .page padding already clears
 *  the tab nav; this bar uses position: fixed bottom: tabBar+gap).
 *  Two buttons: Cancel + Download N. The download button is
 *  disabled with count === 0 so the driver can't fire an empty
 *  request. */
function ReceiptsBundleActionBar({
  count, busy, onCancel, onDownload,
}: {
  count: number;
  busy: boolean;
  onCancel: () => void;
  onDownload: () => void;
}) {
  const { t } = useTranslation('history');
  return (
    <div className={styles.actionBar} role="toolbar" aria-label={t('a11y.receiptBundleActions')}>
      <span className={styles.actionBarCount}>
        {t('bundle.selected', { n: count })}
      </span>
      <div className={styles.actionBarButtons}>
        <button
          type="button"
          className={styles.actionBarCancel}
          onClick={onCancel}
          disabled={busy}
        >
          {t('bundle.cancel')}
        </button>
        <button
          type="button"
          className={styles.actionBarDownload}
          onClick={onDownload}
          disabled={busy || count === 0}
        >
          {busy ? (
            <Loader2 size={14} className={styles.exportSpin} aria-hidden />
          ) : (
            <FileArchive size={14} aria-hidden />
          )}
          {busy ? t('bundle.bundling') : t('bundle.download', { n: count })}
        </button>
      </div>
    </div>
  );
}

function DateChip({ status }: { status: SessionStatus }) {
  const isCompleted = COMPLETED_STATUSES.has(status);
  return (
    <span
      className={`${styles.dateChip} ${isCompleted ? styles.dateChipGood : styles.dateChipWarn}`}
      aria-hidden
    >
      <NamedIcon name="calendar" fallback={<Calendar size={16} />} size={16} />
    </span>
  );
}

/** Maps a raw session status to its i18n key under the `status`
 *  group. Returns '' for statuses we don't have a label for, so
 *  the caller can fall back to the raw (dynamic) status string. */
function statusLabelKey(s: string): string {
  if (s === 'active') return 'status.active';
  if (s === 'failed') return 'status.canceled';
  if (s === 'completed') return 'status.completed';
  if (s === 'stopped_by_user') return 'status.stopped';
  if (s === 'stopped_by_system') return 'status.stopped';
  if (s === 'pending') return 'status.pending';
  return '';
}

// ── Activity tab ───────────────────────────────────────────────────
//
// Unified time-ordered feed across sessions + wallet top-ups +
// points ledger + coupon redemptions. Reads /me/activity with
// type filter chips + period selector. Per-type rows render
// inline (no detail sheet) except sessions, which open the
// existing detail sheet so the tax-prep flow stays intact.

const ACTIVITY_TYPE_ORDER: ActivityType[] = ['session', 'wallet_topup', 'points', 'coupon'];

function ActivityTab({
  range, period, customFrom, customTo,
  setPeriod, setCustomFrom, setCustomTo,
  onOpenSession,
  selectedTypes, setSelectedTypes,
  hasAnySessions,
}: {
  range: { from: string | null; to: string | null };
  period: Period;
  customFrom: string | null;
  customTo: string | null;
  setPeriod: (p: Period) => void;
  setCustomFrom: (v: string | null) => void;
  setCustomTo: (v: string | null) => void;
  /** Tap on a session row inside the unified feed opens the
   *  existing Sessions-tab detail sheet via the parent's
   *  shared detailId state. */
  onOpenSession: (sessionId: string) => void;
  /** Type filter state lifted to the parent so it survives tab
   *  switches. Tapping a chip toggles inclusion; the set always
   *  retains at least one type so the server has something to
   *  query. */
  selectedTypes: Set<ActivityType>;
  setSelectedTypes: React.Dispatch<React.SetStateAction<Set<ActivityType>>>;
  /** Driver-has-any-sessions signal from the parent. Drives the
   *  inline "Show all time" hint inside the empty-period state. */
  hasAnySessions: boolean;
}) {
  const { t } = useTranslation('history');
  const toggleType = (type: ActivityType): void => {
    setSelectedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type) && next.size > 1) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  // Pagination + accumulation buffer — same 5-at-a-time Load
  // More pattern as the Sessions tab list (PAGE_SIZE matched
  // intentionally). Resets when filters or range change so the
  // CTA stays meaningful for the current view.
  const PAGE_SIZE = 5;
  const [page, setPage] = useState(1);
  const [accumulated, setAccumulated] = useState<ActivityEntry[]>([]);
  const [total, setTotal] = useState(0);

  // ── Activity tab dashboard summary ─────────────────────────
  // Cache key includes the range so period changes refetch
  // cleanly + back/forth between presets reads from cache
  // (React Query staleTime of 30s).
  const summaryQ = useQuery({
    queryKey: ['me', 'activity', 'summary', range.from, range.to],
    queryFn: () => fetchMyActivitySummary({ from: range.from, to: range.to }),
    staleTime: 30_000,
  });

  // Memoize the types parameter as a stable, sorted array so
  // React Query's queryKey doesn't re-fire on Set instance churn.
  const typesArray = useMemo(
    () => ACTIVITY_TYPE_ORDER.filter((t) => selectedTypes.has(t)),
    [selectedTypes],
  );

  // Reset to page 1 on any filter / range change. Reset must
  // also clear the accumulator otherwise we'd append fresh
  // results onto stale ones.
  useEffect(() => {
    setPage(1);
    setAccumulated([]);
    setTotal(0);
  }, [typesArray, range.from, range.to]);

  const activityQ = useQuery({
    queryKey: ['me', 'activity', page, PAGE_SIZE, typesArray.join(','), range.from, range.to],
    queryFn: () => fetchMyActivity({
      page, limit: PAGE_SIZE,
      types: typesArray,
      from: range.from, to: range.to,
    }),
    staleTime: 30_000,
    enabled: typesArray.length > 0,
  });

  useEffect(() => {
    if (!activityQ.data) return;
    setTotal(activityQ.data.meta.total);
    if (page === 1) {
      setAccumulated(activityQ.data.data);
    } else {
      setAccumulated((prev) => {
        const seen = new Set(prev.map((r) => r._id));
        const next = [...prev];
        for (const r of activityQ.data!.data) {
          if (!seen.has(r._id)) next.push(r);
        }
        return next;
      });
    }
  }, [activityQ.data, page]);

  const canLoadMore = accumulated.length < total;

  return (
    <>
      {/* Period selector — shared concept with Sessions tab,
          rendered separately here so the activity tab owns its
          range UX without coupling to the dashboard layout. */}
      <div className={styles.periodSelector} role="tablist" aria-label={t('a11y.dateRange')}>
        <PeriodChip label={t('period.d7')} value="7d" active={period === '7d'} onClick={setPeriod} />
        <PeriodChip label={t('period.d30')} value="30d" active={period === '30d'} onClick={setPeriod} />
        <PeriodChip label={t('period.d90')} value="90d" active={period === '90d'} onClick={setPeriod} />
        <PeriodChip label={t('period.y1')} value="1y" active={period === '1y'} onClick={setPeriod} />
        <PeriodChip label={t('period.all')} value="all" active={period === 'all'} onClick={setPeriod} />
        <PeriodChip label={t('period.custom')} value="custom" active={period === 'custom'} onClick={setPeriod} />
      </div>
      {period === 'custom' && (
        <div className={styles.customRow}>
          <input
            type="date"
            aria-label={t('a11y.fromDate')}
            className={styles.customInput}
            value={customFrom ?? ''}
            onChange={(e) => setCustomFrom(e.target.value || null)}
          />
          <span className={styles.customSep}>→</span>
          <input
            type="date"
            aria-label={t('a11y.toDate')}
            className={styles.customInput}
            value={customTo ?? ''}
            onChange={(e) => setCustomTo(e.target.value || null)}
          />
        </div>
      )}

      {/* Activity dashboard widgets — hero (net points change)
          + 4-card stat grid. Same chrome family as the Sessions
          tab dashboard so the two surfaces feel like siblings;
          different metrics tuned for the unified ledger view. */}
      <ActivityDashboardSection summaryQ={summaryQ} period={period} />

      {/* Type filter chips. Multi-select; tapping toggles the
          type into / out of the active set. Always at least one
          type stays active so the server has something to query. */}
      <div className={styles.typeChips} role="group" aria-label={t('a11y.activityTypes')}>
        {ACTIVITY_TYPE_ORDER.map((type) => (
          <button
            key={type}
            type="button"
            aria-pressed={selectedTypes.has(type)}
            className={`${styles.typeChip} ${selectedTypes.has(type) ? styles.typeChipActive : ''}`}
            onClick={() => toggleType(type)}
          >
            {t(`activityType.${type}`)}
          </button>
        ))}
      </div>

      {/* Activity feed list */}
      <div className={styles.list}>
        {activityQ.isLoading && accumulated.length === 0 ? (
          <p className={styles.empty}>
            <Loader2 size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
            {t('activity.loading')}
          </p>
        ) : activityQ.isError ? (
          <p className={styles.empty}>{t('activity.loadError')}</p>
        ) : accumulated.length === 0 ? (
          <p className={styles.empty}>
            {t('activity.empty')}
            {period !== 'all' && hasAnySessions && (
              <>
                {' '}
                <button
                  type="button"
                  className={styles.emptyLink}
                  onClick={() => setPeriod('all')}
                >
                  {t('common.showAllTime')}
                </button>
              </>
            )}
          </p>
        ) : (
          <>
            {accumulated.map((entry) => (
              <ActivityRow
                key={entry._id}
                entry={entry}
                onOpenSession={onOpenSession}
              />
            ))}
            {canLoadMore && (
              <button
                type="button"
                className={styles.loadMoreBtn}
                disabled={activityQ.isFetching}
                onClick={() => setPage((p) => p + 1)}
              >
                {activityQ.isFetching ? (
                  <>
                    <Loader2 size={14} className={styles.exportSpin} aria-hidden />
                    {t('common.loading')}
                  </>
                ) : (
                  <>
                    {t('common.loadMore')}
                    <span className={styles.loadMoreCount}>
                      {t('common.remaining', { n: total - accumulated.length })}
                    </span>
                  </>
                )}
              </button>
            )}
          </>
        )}
      </div>
    </>
  );
}

/** Activity row dispatcher. Switches on the discriminated-union
 *  `type` field and renders the appropriate per-type row
 *  component. Sessions delegate to the existing onOpenSession
 *  callback so the detail sheet still opens; everything else
 *  renders inline summary only (per founder spec — no drill-in
 *  surfaces for points / coupons in v1). */
/**
 * Activity tab dashboard widgets — hero card with net points
 * change in the period + 4-card stat grid (points earned,
 * points redeemed, wallet top-ups $, coupons redeemed count).
 *
 * Parallel structure to DashboardSection on the Sessions tab
 * but tuned for the unified-ledger view: no $-spent hero, no
 * top-locations widget. The hero metric is signed — positive
 * (earned more than redeemed/expired) reads in accent green;
 * negative (redeemed/expired more than earned) reads in the
 * warn token so the driver sees a balance regression at a
 * glance.
 *
 * Skeleton on first load to prevent layout shift; zero values
 * render inline when the period is genuinely empty (matches
 * the Sessions tab's always-render-widgets contract).
 */
function ActivityDashboardSection({
  summaryQ,
  period,
}: {
  summaryQ: { data?: ActivitySummary; isLoading: boolean; isError: boolean };
  period: Period;
}) {
  const { t } = useTranslation('history');
  if (summaryQ.isLoading && !summaryQ.data) {
    return (
      <section className={styles.dashboard} aria-label={t('a11y.activitySummary')}>
        <div className={`${styles.heroCard} ${styles.skeleton}`} aria-hidden />
        <div className={styles.statGrid}>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className={`${styles.statCard} ${styles.skeleton}`} aria-hidden />
          ))}
        </div>
      </section>
    );
  }

  if (summaryQ.isError) {
    return (
      <section className={styles.dashboard} aria-label={t('a11y.activitySummary')}>
        <p className={styles.dashboardError}>
          {t('activityDashboard.summaryError')}
        </p>
      </section>
    );
  }

  const s = summaryQ.data;
  if (!s) return null;

  const periodLabel = t(PERIOD_LABELS[period]);
  // Net change formatting — signed display; negative renders
  // the minus inline. The `.heroValueNegative` class flips the
  // color to the warn token for at-a-glance regression read.
  const netSigned = s.netPointsChange;
  const netLabel = `${netSigned >= 0 ? '+' : '−'}${Math.abs(netSigned).toLocaleString()}`;

  return (
    <section className={styles.dashboard} aria-label={t('a11y.activitySummary')}>
      <div className={styles.heroCard}>
        <span className={styles.heroEyebrow}>{t('activityDashboard.netPoints', { period: periodLabel })}</span>
        <div className={styles.heroValueRow}>
          <span
            className={`${styles.heroValue} ${netSigned < 0 ? styles.heroValueNegative : ''}`}
          >
            {netLabel}
          </span>
          <span className={styles.heroSubtitle}>pts</span>
        </div>
        <span className={styles.heroSubtitle}>
          {netSigned >= 0
            ? t('activityDashboard.earnedMore')
            : t('activityDashboard.redeemedMore')}
        </span>
      </div>

      {/* 4-card stat grid. Tighter than Sessions' 5-card grid;
          mobile reflows naturally as 2x2. */}
      <div className={styles.statGrid}>
        <StatCard
          label={t('activityDashboard.statPointsEarned')}
          value={s.pointsEarned > 0 ? `+${s.pointsEarned.toLocaleString()}` : '0'}
        />
        <StatCard
          label={t('activityDashboard.statPointsRedeemed')}
          value={s.pointsRedeemed > 0 ? `−${s.pointsRedeemed.toLocaleString()}` : '0'}
        />
        <StatCard label={t('activityDashboard.statWalletTopups')} value={formatCents(s.walletTopupCents)} />
        <StatCard
          label={t('activityDashboard.statCouponsUsed')}
          value={s.couponsRedeemedCount.toLocaleString()}
        />
      </div>
    </section>
  );
}

function ActivityRow({
  entry, onOpenSession,
}: {
  entry: ActivityEntry;
  onOpenSession: (sessionId: string) => void;
}) {
  switch (entry.type) {
    case 'session':
      return (
        <ActivitySessionRow
          payload={entry.payload}
          occurredAt={entry.occurred_at}
          onOpen={() => onOpenSession(String(entry.payload._id))}
        />
      );
    case 'wallet_topup':
      return (
        <ActivityWalletRow
          payload={entry.payload}
          occurredAt={entry.occurred_at}
        />
      );
    case 'points':
      return (
        <ActivityPointsRow
          payload={entry.payload}
          occurredAt={entry.occurred_at}
        />
      );
    case 'coupon':
      return (
        <ActivityCouponRow
          payload={entry.payload}
          occurredAt={entry.occurred_at}
        />
      );
  }
}

function ActivitySessionRow({
  payload, occurredAt, onOpen,
}: {
  payload: ActivityEntry & { type: 'session' } extends infer E
    ? E extends { payload: infer P } ? P : never
    : never;
  occurredAt: string;
  onOpen: () => void;
}) {
  const { t } = useTranslation('history');
  const isCompleted = COMPLETED_STATUSES.has(payload.status);
  const isCancelled = payload.status === 'failed';
  const tone = isCompleted ? 'good' : isCancelled ? 'warn' : 'mute';
  const fallbackKey = statusLabelKey(payload.status);
  const label = isCompleted
    ? t('status.charging')
    : isCancelled
      ? t('status.canceled')
      : (fallbackKey ? t(fallbackKey) : payload.status);
  const totalCents = Math.round(payload.cost?.total ?? 0);
  return (
    <button type="button" className={styles.row} onClick={onOpen}>
      <span className={`${styles.activityIcon} ${styles.activityIconSession}`} aria-hidden>
        <NamedIcon name="calendar" fallback={<Calendar size={16} />} size={16} />
      </span>
      <div className={styles.rowInfo}>
        <p className={styles.rowDate}>
          {label} · {formatDateLabel(occurredAt, t)}
        </p>
        <p className={styles.rowMeta}>
          {formatTime(occurredAt)}
          {payload.charger_identity ? ` · ${payload.charger_identity}` : ''}
        </p>
      </div>
      <div className={styles.rowRight}>
        <StatusPill tone={tone} variant="soft" size="sm">{t('pill.session')}</StatusPill>
        {/* Re-framed in 2025-Q4 — was prefixed with a unicode
            minus to convey "wallet debit", but that read as a
            negative number bug to drivers (the session-detail
            panel showed the same value as a positive TOTAL COST).
            Top-up + points + coupon rows above all carry their
            own '+' for credits via `rowAmountCredit`; session
            debits now render positive + muted via `rowAmountDebit`
            so direction reads from style, not from a leading sign. */}
        <span className={`${styles.rowAmount} ${styles.rowAmountDebit}`}>
          {isCompleted && totalCents > 0
            ? formatCents(totalCents, payload.cost?.currency ?? 'USD')
            : '—'}
        </span>
        <WalletFxSub session={payload} />
      </div>
    </button>
  );
}

function ActivityWalletRow({
  payload, occurredAt,
}: {
  payload: ActivityWalletTopupPayload;
  occurredAt: string;
}) {
  const { t } = useTranslation('history');
  // Top-up rows show the total wallet credit (the amount the
  // wallet GAINED, including coupon + tier bonuses) on the right.
  // The amount the card was charged is smaller print as supporting
  // detail in the meta line.
  const totalCreditCents = payload.response.totalCreditCents
    ?? payload.response.chargedCents
    ?? payload.payload.requestedCents
    ?? 0;
  const chargedCents = payload.response.chargedCents ?? 0;
  const bonusCents = (payload.response.bonusCreditCents ?? 0)
    + (payload.response.tierBonusCents ?? 0);
  // The wallet's currency (a CAD top-up is CA$); older rows are USD.
  const currency = payload.response.currency ?? 'USD';
  return (
    <div className={styles.row}>
      <span className={`${styles.activityIcon} ${styles.activityIconWallet}`} aria-hidden>
        <NamedIcon name="wallet" fallback={<Wallet size={16} />} size={16} />
      </span>
      <div className={styles.rowInfo}>
        <p className={styles.rowDate}>
          {t('wallet.topupTitle')} · {formatDateLabel(occurredAt, t)}
        </p>
        <p className={styles.rowMeta}>
          {formatTime(occurredAt)}
          {chargedCents > 0
            ? ` · ${t('wallet.charged', { amount: formatCents(chargedCents, currency) })}`
            : ''}
          {bonusCents > 0
            ? ` · ${t('wallet.bonus', { amount: formatCents(bonusCents, currency) })}`
            : ''}
        </p>
      </div>
      <div className={styles.rowRight}>
        <StatusPill tone="good" variant="soft" size="sm">{t('pill.topup')}</StatusPill>
        <span className={`${styles.rowAmount} ${styles.rowAmountCredit}`}>
          +{formatCents(totalCreditCents, currency)}
        </span>
      </div>
    </div>
  );
}

function ActivityPointsRow({
  payload, occurredAt,
}: {
  payload: ActivityPointsPayload;
  occurredAt: string;
}) {
  const { t } = useTranslation('history');
  const label = t(`pointsEvent.${payload.event_type}`, { defaultValue: payload.event_type });
  const isPositive = payload.points_delta >= 0;
  const formatted = `${isPositive ? '+' : ''}${payload.points_delta.toLocaleString()} pts`;
  return (
    <div className={styles.row}>
      <span className={`${styles.activityIcon} ${styles.activityIconPoints}`} aria-hidden>
        <NamedIcon name="sparkles" fallback={<Sparkles size={16} />} size={16} />
      </span>
      <div className={styles.rowInfo}>
        <p className={styles.rowDate}>
          {label} · {formatDateLabel(occurredAt, t)}
        </p>
        <p className={styles.rowMeta}>
          {formatTime(occurredAt)}
          {payload.note ? ` · ${payload.note}` : ''}
          {' · '}
          {t('points.balance', { balance: payload.balance_after.toLocaleString() })}
        </p>
      </div>
      <div className={styles.rowRight}>
        <StatusPill tone={isPositive ? 'good' : 'mute'} variant="soft" size="sm">
          {t('pill.points')}
        </StatusPill>
        <span className={`${styles.rowAmount} ${isPositive ? styles.rowAmountCredit : ''}`}>
          {formatted}
        </span>
      </div>
    </div>
  );
}

function ActivityCouponRow({
  payload, occurredAt,
}: {
  payload: ActivityCouponPayload;
  occurredAt: string;
}) {
  const { t } = useTranslation('history');
  // Coupon rows surface the discount + the bonus points (when
  // the coupon awards both — one row covers both incentives so
  // the timeline doesn't double up the redemption.)
  const discountCents = payload.discount_applied_cents ?? 0;
  const bonusPoints = payload.bonus_points_awarded ?? 0;
  const statusLabel = payload.status === 'applied' ? t('coupon.applied') : titleCaseSimple(payload.status);
  return (
    <div className={styles.row}>
      <span className={`${styles.activityIcon} ${styles.activityIconCoupon}`} aria-hidden>
        <NamedIcon name="ticket" fallback={<Ticket size={16} />} size={16} />
      </span>
      <div className={styles.rowInfo}>
        <p className={styles.rowDate}>
          {t('coupon.title', { code: payload.coupon_code })} · {formatDateLabel(occurredAt, t)}
        </p>
        <p className={styles.rowMeta}>
          {formatTime(occurredAt)}
          {' · '}
          {statusLabel}
          {payload.applied_to_type
            ? ` · ${payload.applied_to_type === 'wallet_topup' ? t('coupon.appliedToTopup') : t('coupon.appliedToSession')}`
            : ''}
        </p>
      </div>
      <div className={styles.rowRight}>
        <StatusPill tone="accent" variant="soft" size="sm">{t('pill.coupon')}</StatusPill>
        <span className={`${styles.rowAmount} ${styles.rowAmountCredit}`}>
          {discountCents > 0
            ? `+${formatCents(discountCents)}`
            : bonusPoints > 0
              ? `+${bonusPoints.toLocaleString()} pts`
              : '—'}
        </span>
      </div>
    </div>
  );
}

/** Sentence-case a status string. Differs from statusLabelKey
 *  above which is session-specific; this one handles arbitrary
 *  short (dynamic) server status strings ('claimed' → 'Claimed'). */
function titleCaseSimple(s: string): string {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

// ── Detail sheet ───────────────────────────────────────────────────
//
// Slides up over the list when a row is tapped. Fetches full
// detail (location_name, charger_label) on open via
// fetchSession(id) — list response is sparser than the sheet
// needs. AvatarPicker pattern: backdrop click closes, inner
// content stopPropagation, slide-up animation, safe-area pad.
function DetailSheet({
  sessionId, onClose,
}: { sessionId: string; onClose: () => void }) {
  const { t } = useTranslation('history');
  const detailQ = useQuery<SessionDetail>({
    queryKey: ['session', sessionId, 'detail'],
    queryFn: () => fetchSession(sessionId),
    staleTime: 60_000,
    refetchInterval: query => query.state.data?.idle_view?.cable_connected || query.state.data?.idle_view?.settlement_pending ? 5000 : false,
  });

  return (
    <div
      role="dialog"
      aria-label={t('a11y.sessionDetail')}
      className={styles.sheetBackdrop}
      onClick={onClose}
    >
      <div className={styles.sheet} onClick={(e) => e.stopPropagation()}>
        {detailQ.isLoading || !detailQ.data ? (
          <DetailSheetLoading onClose={onClose} />
        ) : (
          <DetailSheetBody session={detailQ.data} onClose={onClose} />
        )}
      </div>
    </div>
  );
}

function DetailSheetLoading({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('history');
  return (
    <>
      <header className={styles.sheetHeader}>
        <div className={styles.sheetHeaderLeft}>
          <p className={styles.sheetDate}>{t('detail.loading')}</p>
        </div>
        <div className={styles.sheetHeaderRight}>
          <button
            type="button"
            className={styles.sheetClose}
            aria-label={t('a11y.close')}
            onClick={onClose}
          >
            <X size={16} />
          </button>
        </div>
      </header>
      <p className={styles.empty}>
        <Loader2 size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
        {t('detail.loadingDetail')}
      </p>
    </>
  );
}

function DetailSheetBody({
  session, onClose,
}: { session: SessionDetail; onClose: () => void }) {
  const { t, i18n } = useTranslation('history');
  const idleFacts = idleSummaryFacts(session);
  const isCompleted = COMPLETED_STATUSES.has(session.status);
  const totalCents = Math.round(session.cost?.total ?? 0);
  // A start that never happened carries no charge and says so.
  const noCharge = sessionNeverStartedNoCharge(session);
  const billedToFleet = session.billed_to === 'fleet';
  // Home-session framing: sessions at the caller's own home charger are
  // never billed by EVI — the cost stat carries the utility estimate.
  const home = useHomeSession(session);
  // Real per-session points. Skipped for fleet-sponsored (always zero,
  // the copy branches on billed_to) and not-yet-completed sessions.
  const pointsQ = useQuery<SessionPoints>({
    queryKey: ['session', session._id, 'points'],
    queryFn: () => fetchSessionPoints(session._id),
    enabled: isCompleted && !billedToFleet,
    staleTime: 60_000,
  });
  const stationLine = session.location_name
    ? `${session.location_name}${session.charger_identity ? ` · ${session.charger_identity}` : ''}`
    : (session.charger_identity ?? '—');
  // Open the selected session's server-backed receipt, not the
  // Stripe page for the unused portion of its authorization hold.
  const hasReceipt = isCompleted;
  // Download Receipt — surfaces the client-side pdf-lib renderer
  // on every completed session, including $0/covered/coupon-zeroed
  // ones. Fleet drivers need receipts for accounting regardless of
  // whether a personal-card charge ran. Parity with the same gate
  // on SessionDetailPage (the two surfaces reach the same PDF via
  // the same helper). Local state mirrors the SessionDetailPage
  // handler shape — disabling while in flight + surfacing errors
  // through the existing toast helper.
  const [downloadingReceipt, setDownloadingReceipt] = useState(false);
  const handleDownloadReceipt = async (): Promise<void> => {
    if (downloadingReceipt) return;
    setDownloadingReceipt(true);
    try {
      await downloadSessionReceipt(
        session,
        home.isHomeSession
          ? { estimateCents: home.estimateCents, rateSummary: home.rateSummary, currency: home.currency }
          : undefined,
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('detail.receiptError');
      toast(msg, { tone: 'error' });
    } finally {
      setDownloadingReceipt(false);
    }
  };

  return (
    <>
      <header className={styles.sheetHeader}>
        <div className={styles.sheetHeaderLeft}>
          <p className={styles.sheetDate}>{formatDateLabel(session.started_at, t)}</p>
          <p className={styles.sheetTime}>{formatTime(session.started_at)} · {stationLine}</p>
        </div>
        <div className={styles.sheetHeaderRight}>
          {hasReceipt && (
            <button
              type="button"
              className={styles.sheetInvoice}
              onClick={() => {
                window.open(sessionReceiptPath(session._id), '_blank', 'noopener,noreferrer');
              }}
            >
              {t('detail.viewInvoice')} <ExternalLink size={12} />
            </button>
          )}
          {isCompleted && (
            <button
              type="button"
              className={styles.sheetInvoice}
              disabled={downloadingReceipt}
              onClick={() => { void handleDownloadReceipt(); }}
            >
              {downloadingReceipt
                ? <>{t('detail.generating')} <Loader2 size={12} className={styles.exportSpin} aria-hidden /></>
                : <>{t('detail.downloadReceipt')} <Download size={12} /></>}
            </button>
          )}
          <button
            type="button"
            className={styles.sheetClose}
            aria-label={t('a11y.close')}
            onClick={onClose}
          >
            <X size={16} />
          </button>
        </div>
      </header>

      <div className={styles.statGrid} style={idleFacts ? { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' } : undefined}>
        <div className={styles.stat}>
          <p className={styles.statLabel}>{t('detail.energy')}</p>
          <p className={styles.statValue}>
            {session.energy_kwh.toFixed(2)}
            <span className={styles.statUnit}> kWh</span>
          </p>
        </div>
        <div className={styles.stat}>
          <p className={styles.statLabel}>{t('detail.duration')}</p>
          <p className={styles.statValue}>{formatDurationMinutes(session.duration_minutes)}</p>
        </div>
        {idleFacts && <div className={styles.stat} style={{ gridColumn: '1 / -1' }}><IdleSummaryLine facts={idleFacts} locale={i18n.language} /></div>}
        {home.isHomeSession ? (
          // Home sessions: EVI billed nothing — show the utility
          // ESTIMATE (their entered rate) instead of a dash/$0.
          <div className={styles.stat}>
            <p className={styles.statLabel}>{t('detail.estUtilityCost')}</p>
            <p className={styles.statValue}>
              {home.estimateCents != null
                ? formatCents(Math.round(home.estimateCents), home.currency ?? session.cost?.currency ?? 'USD')
                : t('detail.free')}
            </p>
          </div>
        ) : (
          <div className={styles.stat}>
            <p className={styles.statLabel}>{t('detail.totalCost')}</p>
            <p className={styles.statValue}>
              {noCharge
                ? t('detail.noCharge')
                : totalCents > 0
                ? formatCents(totalCents, session.cost?.currency ?? 'USD')
                : '—'}
              {!noCharge && totalCents > 0 && (
                <span className={styles.statUnit}> {session.cost?.currency ?? 'USD'}</span>
              )}
            </p>
            <WalletFxSub session={session} />
          </div>
        )}
      </div>

      {home.isHomeSession && <p className={styles.footerNote}>{t('detail.utilityEstimateNote')}</p>}

      <div className={styles.sessionIdRow}>
        <span className={styles.sessionIdLabel}>{t('detail.sessionId')}</span>
        <span className={styles.sessionIdValue}>{session._id}</span>
      </div>

      <p className={styles.footerNote}>
        {sessionPointsNote(isCompleted, billedToFleet, pointsQ.isLoading, pointsQ.data)}
      </p>
    </>
  );
}
